import { makeAutoObservable, runInAction } from 'mobx';
import { adminEmployeesGetList } from '@/services/employeesApi';
import {
  adminShiftsCreate,
  adminShiftsDelete,
  adminShiftsGetList,
  adminShiftsGetOne,
  adminShiftsUpdate,
} from '@/services/shiftsApi';
import { shiftsStore } from './ShiftsStore';
import { shiftTemplatesStore } from './ShiftTemplatesStore';
import { toastStore } from './ToastStore';
import type { EmployeeListItem } from '@/types/employee.types';
import type { ShiftDay } from '@/types/shift.types';
import { AppError } from '@/utils/errors';
import {
  addDraftEmployee,
  addDraftRow,
  applyTemplateToDraft,
  completeClock,
  completeDraftTimes,
  draftFromDetail,
  emptyShiftDraftErrors,
  hasShiftDraftErrors,
  removeDraftRow,
  setDraftRowEmployee,
  shouldAbandonEmptyCreatedShift,
  toUpdatePayload,
  validateShiftDraft,
  type PlannedTimeBounds,
  type ShiftDraft,
  type ShiftDraftErrors,
} from './shiftDraftModel';

const EMPLOYEE_PAGE_LIMIT = 200;
export const SHIFT_DATE_TAKEN = 'На эту дату смена уже создана';

export type CreateShiftResult =
  | { kind: 'created'; uuid: string }
  | { kind: 'conflict'; existing: ShiftDay | null }
  | { kind: 'error' };

class ShiftDraftStore {
  employees: EmployeeListItem[] = [];
  employeesLoading = false;
  employeesError: string | null = null;

  draft: ShiftDraft | null = null;
  isLoadingDraft = false;
  loadError: string | null = null;
  isSaving = false;
  isCreating = false;
  submitError: string | null = null;
  createError: string | null = null;
  fieldErrors: ShiftDraftErrors = emptyShiftDraftErrors();

  private employeesLoadedFor: string | null = null;
  private draftRequestId = 0;
  private initialServerMemberCount = 0;
  /** После PATCH или загрузки существующей смены отмена не удаляет день. */
  private persistedOnServer = false;
  /** Успешный DELETE пустой created-смены в этом сеансе — повторно не удаляем. */
  private emptyCreatedAbandoned = false;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Список для селектов редактора. Не идёт через employeesStore.setSearch:
   * тот затирает фильтр вкладки сотрудников.
   */
  loadEmployees = async (serviceCenterUuid: string, options?: { force?: boolean }) => {
    if (!serviceCenterUuid) return;
    if (!options?.force && this.employeesLoadedFor === serviceCenterUuid) return;

    this.employeesLoading = true;
    try {
      const items = await fetchEmployees(serviceCenterUuid);
      runInAction(() => {
        this.employees = items;
        this.employeesLoadedFor = serviceCenterUuid;
        this.employeesError = null;
        this.employeesLoading = false;
      });
    } catch {
      runInAction(() => {
        this.employees = [];
        this.employeesLoadedFor = null;
        this.employeesError = 'Не удалось загрузить сотрудников';
        this.employeesLoading = false;
      });
      toastStore.showError('Не удалось загрузить сотрудников');
    }
  };

  clearCreateError = () => {
    this.createError = null;
  };

  createShift = async (params: {
    serviceCenterUuid: string;
    date: string;
    templateUuid: string | null;
  }): Promise<CreateShiftResult> => {
    if (this.isCreating) return { kind: 'error' };

    this.isCreating = true;
    this.createError = null;
    try {
      const shift = await adminShiftsCreate({
        service_center_uuid: params.serviceCenterUuid,
        date: params.date,
        ...(params.templateUuid ? { template_uuid: params.templateUuid } : {}),
      });
      runInAction(() => {
        this.draft = draftFromDetail(shift);
        this.initialServerMemberCount = shift.members?.length ?? 0;
        this.persistedOnServer = false;
        this.fieldErrors = emptyShiftDraftErrors();
        this.submitError = null;
        this.isCreating = false;
        this.isLoadingDraft = false;
        this.loadError = null;
      });
      return { kind: 'created', uuid: shift.uuid };
    } catch (error) {
      if (error instanceof AppError && error.statusCode === 409) {
        let existing: ShiftDay | null = null;
        try {
          const list = await adminShiftsGetList({
            service_center_uuid: params.serviceCenterUuid,
            from: params.date,
            to: params.date,
          });
          existing = list.data?.[0] ?? null;
        } catch {
          existing = null;
        }
        runInAction(() => {
          this.isCreating = false;
          this.createError = SHIFT_DATE_TAKEN;
        });
        return { kind: 'conflict', existing };
      }

      runInAction(() => {
        this.isCreating = false;
        this.createError = 'Не удалось создать смену';
      });
      return { kind: 'error' };
    }
  };

  loadDraft = async (uuid: string) => {
    if (this.draft?.uuid === uuid) return;

    const requestId = ++this.draftRequestId;
    this.isLoadingDraft = true;
    this.loadError = null;
    this.draft = null;
    this.fieldErrors = emptyShiftDraftErrors();
    this.submitError = null;

    try {
      const detail = await adminShiftsGetOne(uuid);
      if (requestId !== this.draftRequestId) return;
      runInAction(() => {
        this.draft = draftFromDetail(detail);
        this.initialServerMemberCount = detail.members?.length ?? 0;
        this.persistedOnServer = true;
        this.isLoadingDraft = false;
        this.loadError = null;
      });
    } catch {
      if (requestId !== this.draftRequestId) return;
      runInAction(() => {
        this.isLoadingDraft = false;
        this.loadError = 'Не удалось загрузить смену';
      });
    }
  };

  discard = () => {
    this.draftRequestId += 1;
    this.draft = null;
    this.initialServerMemberCount = 0;
    this.persistedOnServer = false;
    this.emptyCreatedAbandoned = false;
    this.fieldErrors = emptyShiftDraftErrors();
    this.submitError = null;
    this.isLoadingDraft = false;
    this.isSaving = false;
    this.loadError = null;
  };

  /**
   * Uuid пустой created-смены, которую ещё можно снять с сервера.
   * Снимок до discard(): DELETE идёт по этому uuid, а не по уже обнулённому draft.
   */
  peekAbandonUuid = (): string | null => {
    if (this.emptyCreatedAbandoned || this.persistedOnServer || this.isSaving) return null;
    const draft = this.draft;
    if (!draft || !shouldAbandonEmptyCreatedShift(draft, this.initialServerMemberCount)) {
      return null;
    }
    return draft.uuid;
  };

  deleteAbandonedShift = async (uuid: string): Promise<boolean> => {
    try {
      await adminShiftsDelete(uuid);
    } catch (error) {
      if (!(error instanceof AppError && error.statusCode === 404)) {
        return false;
      }
    }
    runInAction(() => {
      this.emptyCreatedAbandoned = true;
    });
    shiftsStore.removeShift(uuid);
    return true;
  };

  /**
   * Отмена без «Сохранить»: пустая created-смена, созданная в этом сеансе, убирается с сервера.
   */
  abandonEmptyCreatedShiftIfNeeded = async (): Promise<boolean> => {
    const uuid = this.peekAbandonUuid();
    if (!uuid) return false;
    return this.deleteAbandonedShift(uuid);
  };

  setResponsible = (employeeUuid: string | null) => {
    if (!this.draft || this.draft.status === 'closed') return;
    this.draft = { ...this.draft, responsibleEmployeeUuid: employeeUuid };
    this.fieldErrors = { ...this.fieldErrors, responsible: undefined };
  };

  setSelectedTemplate = (templateUuid: string | null) => {
    if (!this.draft || this.draft.status === 'closed') return;
    this.draft = { ...this.draft, selectedTemplateUuid: templateUuid };
  };

  /** Заполняет черновик из выбранного шаблона. PATCH не отправляет. */
  applySelectedTemplate = () => {
    if (!this.draft || this.draft.status === 'closed') return;
    const selected = this.draft.selectedTemplateUuid;
    if (!selected) {
      this.draft = applyTemplateToDraft(this.draft, null);
      this.fieldErrors = emptyShiftDraftErrors();
      return;
    }
    const template = shiftTemplatesStore.templates.find((item) => item.uuid === selected);
    if (!template) return;
    this.draft = applyTemplateToDraft(this.draft, template);
    this.fieldErrors = emptyShiftDraftErrors();
  };

  addEmployeeRow = () => {
    if (!this.draft) return;
    this.draft = addDraftRow(this.draft);
  };

  addEmployeeToRoster = (employeeUuid: string) => {
    if (!this.draft) return;
    const employee = this.employees.find((item) => item.uuid === employeeUuid);
    this.draft = addDraftEmployee(
      this.draft,
      employeeUuid,
      employee
        ? {
            name: `${employee.last_name} ${employee.first_name}`.trim(),
            position: employee.position.name,
          }
        : undefined,
    );
  };

  removeEmployeeRow = (key: string) => {
    if (!this.draft) return;
    this.draft = removeDraftRow(this.draft, key);
    if (this.fieldErrors.rows[key]) {
      const rows = { ...this.fieldErrors.rows };
      delete rows[key];
      this.fieldErrors = { ...this.fieldErrors, rows };
    }
  };

  setRowEmployee = (key: string, employeeUuid: string | null) => {
    if (!this.draft) return;
    this.draft = setDraftRowEmployee(this.draft, key, employeeUuid);
    this.clearRowError(key);
  };

  setRowTime = (key: string, edge: 'start' | 'end', value: string) => {
    if (!this.draft || this.draft.status === 'closed') return;
    this.draft = {
      ...this.draft,
      rows: this.draft.rows.map((row) => {
        if (row.key !== key) return row;
        return edge === 'start' ? { ...row, plannedStart: value } : { ...row, plannedEnd: value };
      }),
    };
    this.clearRowError(key);
  };

  completeRowTime = (key: string, edge: 'start' | 'end', raw?: string) => {
    if (!this.draft || this.draft.status === 'closed') return;
    const row = this.draft.rows.find((item) => item.key === key);
    if (!row) return;
    const stored = edge === 'start' ? row.plannedStart : row.plannedEnd;
    const completed = completeClock(raw ?? stored);
    if (completed === stored) return;
    this.setRowTime(key, edge, completed);
  };

  setRowScheme = (key: string, templateUuid: string | null) => {
    if (!this.draft || this.draft.status === 'closed') return;
    this.draft = {
      ...this.draft,
      rows: this.draft.rows.map((row) =>
        row.key === key ? { ...row, paymentSchemeTemplateUuid: templateUuid } : row,
      ),
    };
    this.clearRowError(key);
  };

  save = async (bounds?: PlannedTimeBounds | null): Promise<boolean> => {
    if (!this.draft || this.isSaving) return false;
    if (this.draft.status === 'closed') {
      this.fieldErrors = validateShiftDraft(this.draft);
      return false;
    }

    this.draft = completeDraftTimes(this.draft);
    const errors = validateShiftDraft(this.draft, bounds);
    if (hasShiftDraftErrors(errors)) {
      this.fieldErrors = errors;
      return false;
    }

    const uuid = this.draft.uuid;
    const payload = toUpdatePayload(this.draft);
    const generation = this.draftRequestId;
    this.isSaving = true;
    this.submitError = null;
    this.fieldErrors = emptyShiftDraftErrors();

    try {
      await adminShiftsUpdate(uuid, payload);
      if (generation !== this.draftRequestId) return false;
      runInAction(() => {
        this.initialServerMemberCount = this.draft?.rows.filter((row) => row.employeeUuid).length ?? 0;
        this.persistedOnServer = true;
      });
      return true;
    } catch (error) {
      if (generation !== this.draftRequestId) return false;
      runInAction(() => {
        this.submitError =
          error instanceof AppError ? error.message : 'Не удалось сохранить смену';
      });
      return false;
    } finally {
      runInAction(() => {
        if (generation === this.draftRequestId) this.isSaving = false;
      });
    }
  };

  private clearRowError = (key: string) => {
    if (!this.fieldErrors.rows[key]) return;
    const rows = { ...this.fieldErrors.rows };
    delete rows[key];
    this.fieldErrors = { ...this.fieldErrors, rows };
  };
}

const fetchEmployees = async (serviceCenterUuid: string): Promise<EmployeeListItem[]> => {
  const items: EmployeeListItem[] = [];
  let offset = 0;

  for (let page = 0; page < 20; page += 1) {
    const response = await adminEmployeesGetList({
      service_center_uuid: serviceCenterUuid,
      sort: 'last_name',
      order: 'ASC',
      limit: EMPLOYEE_PAGE_LIMIT,
      offset,
    });
    const chunk = response.data ?? [];
    items.push(...chunk);
    offset += chunk.length;
    if (chunk.length === 0 || items.length >= (response.total ?? items.length)) break;
  }

  return items;
};

export const shiftDraftStore = new ShiftDraftStore();
export type { ShiftDraftStore };
